import { FormGroupType, FormValue } from "../../../../../../harmony/angular";
import { Mapper } from "../../../../../../harmony/core";
import { assertUnreachable } from "../../../../../admin/shared/functions/assert-unreachable";
import { PageDto } from "../../../../../features/shared/data/page.parser";
import { Page } from "../../../../../features/shared/domain/models/page";
import { CustomTags } from "../../../../../shared/entities/custom-tags";
import {
  PublicationTemplateEntity,
  PublicationTemplateType,
} from "../../data/entities/publication-templates.entity";
import { GetAvailablePublicationTypesInteractor } from "../interactors/get-publication-template-types.interactor";
import { LinkType } from "../models/facebook-carousel";
import {
  PartialPublicationTemplate,
  PublicationTemplate,
} from "../models/publication-template";
import {
  mapFacebookCarouselEntityToModel,
  mapFacebookPostEntityToModel,
  mapFacebookStoryEntityToModel,
} from "./facebook.mappers";
import { mapGooglePerformanceMaxEntityToModel } from "./google.mappers";
import {
  mapInstagramCarouselEntityToModel,
  mapInstagramSingleImageEntityToModel,
  mapInstagramStoryEntityToModel,
} from "./instagram.mappers";

export type FormToApiMapper<
  Form extends FormGroupType<Form>,
  CreateDto,
> = Mapper<FormValue<Form>, CreateDto>;

export type FormToPreviewMapper<
  Form extends FormGroupType<Form>,
  T extends PublicationTemplateType,
> = Mapper<FormValue<Form>, PartialPublicationTemplate<T>>;

export const getLinkByLinkType = (
  type: LinkType,
  link: string | null,
): string =>
  type === LinkType.CustomUrl && link
    ? link
    : type === LinkType.CampaignUrl
      ? CustomTags.CampaignUrl
      : CustomTags.PublisherUrl;

export class PublicationTemplateEntityToPublicationTemplateMapper
  implements Mapper<PublicationTemplateEntity, PublicationTemplate>
{
  public map(from: PublicationTemplateEntity): PublicationTemplate {
    switch (from.publicationTemplateType) {
      case PublicationTemplateType.InstagramCarousel: {
        return mapInstagramCarouselEntityToModel(from);
      }
      case PublicationTemplateType.InstagramStory: {
        return mapInstagramStoryEntityToModel(from);
      }
      case PublicationTemplateType.InstagramSingleImage: {
        return mapInstagramSingleImageEntityToModel(from);
      }
      case PublicationTemplateType.FacebookCarousel: {
        return mapFacebookCarouselEntityToModel(from);
      }
      case PublicationTemplateType.FacebookPost: {
        return mapFacebookPostEntityToModel(from);
      }
      case PublicationTemplateType.FacebookStory: {
        return mapFacebookStoryEntityToModel(from);
      }
      case PublicationTemplateType.GooglePerformanceMax: {
        return mapGooglePerformanceMaxEntityToModel(from);
      }
      default:
        return assertUnreachable(from as never);
    }
  }
}

export interface IPublicationType {
  readonly publicationTemplateType: PublicationTemplateType;
}

export interface IPublication {
  readonly publicationTemplate: IPublicationType;
}

export class PublicationTypePageMapper<To>
  implements Mapper<PageDto<IPublicationType>, Page<To>>
{
  private readonly availablePublicationTypes =
    new GetAvailablePublicationTypesInteractor().execute();

  constructor(private readonly mapper: Mapper<IPublicationType, To>) {}

  public map(from: PageDto<IPublicationType>): Page<To> {
    return new Page(
      from.pageNumber,
      from.size,
      from.totalSize,
      from.entries
        ? from.entries
            .filter((entry) =>
              filterPublication(
                this.availablePublicationTypes,
                entry.publicationTemplateType,
              ),
            )
            .map((entry) => this.mapper.map(entry))
        : [],
    );
  }
}

export class PublicationPageMapper<To>
  implements Mapper<PageDto<IPublication>, Page<To>>
{
  private readonly availablePublicationTypes =
    new GetAvailablePublicationTypesInteractor().execute();

  constructor(private readonly mapper: Mapper<IPublication, To>) {}

  public map(from: PageDto<IPublication>): Page<To> {
    return new Page(
      from.pageNumber,
      from.size,
      from.totalSize,
      from.entries
        ? from.entries
            .filter((entry) =>
              filterPublication(
                this.availablePublicationTypes,
                entry.publicationTemplate.publicationTemplateType,
              ),
            )
            .map((entry) => this.mapper.map(entry))
        : [],
    );
  }
}

const filterPublication = (
  availableTypes: PublicationTemplateType[],
  type: PublicationTemplateType,
): boolean => {
  const isAvailableType = availableTypes.includes(type);
  if (!isAvailableType) {
    console.error("Publication type not available : ", type);
  }
  return isAvailableType;
};
